import { createSignal, Show } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useAuth } from '../lib/auth'
import { requestPasswordReset } from '../lib/api'

export default function VenueLogin() {
  const navigate = useNavigate()
  const auth = useAuth()

  const [email, setEmail] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [isResetting, setIsResetting] = createSignal(false)
  const [resetEmail, setResetEmail] = createSignal('')
  const [resetSent, setResetSent] = createSignal(false)

  async function handleLogin(e: Event) {
    e.preventDefault()
    const success = await auth.loginAsStaff(email(), password())
    if (success) {
      const venue = auth.activeVenue()
      if (venue) {
        navigate(`/venue/${venue.venueId}`)
      }
    }
  }

  async function handleResetPassword(e: Event) {
    e.preventDefault()
    if (!resetEmail()) return
    const res = await requestPasswordReset(resetEmail())
    if (res.error) {
      alert(res.error.message || 'Failed to send reset email')
    } else {
      setResetSent(true)
    }
  }

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      <header class="bg-white border-b border-gray-200 px-4 py-4">
        <div class="max-w-md mx-auto flex items-center gap-3">
          <img src="/logo.svg" alt="ZipCheck" class="h-8" />
          <div>
            <span class="text-lg font-semibold text-gray-900">ZipCheck</span>
            <span class="ml-2 text-sm text-gray-500">Venue Dashboard</span>
          </div>
        </div>
      </header>

      <main class="flex-1 flex items-center justify-center p-4">
        <div class="w-full max-w-md">
          <Show when={!isResetting()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h1 class="text-xl font-bold text-gray-900 mb-2">Venue login</h1>
              <p class="text-sm text-gray-600 mb-6">
                Sign in to manage your venue, team, and payouts.
              </p>

              <form onSubmit={handleLogin}>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  placeholder="venue@example.com"
                  value={email()}
                  onInput={(e) => setEmail(e.currentTarget.value)}
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base
                         focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                         placeholder:text-gray-400 mb-4"
                />

                <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  value={password()}
                  onInput={(e) => setPassword(e.currentTarget.value)}
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base
                         focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                         placeholder:text-gray-400"
                />

                <Show when={auth.error()}>
                  <p class="mt-2 text-sm text-red-600">{auth.error()}</p>
                </Show>

                <button
                  type="submit"
                  disabled={auth.isLoading() || !email() || !password()}
                  class="mt-4 w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 active:bg-emerald-800
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
                >
                  {auth.isLoading() ? 'Signing in...' : 'Sign in'}
                </button>
              </form>

              <div class="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setIsResetting(true)}
                  class="text-sm text-emerald-600 hover:text-emerald-700"
                >
                  Forgot password?
                </button>
                <button
                  onClick={() => navigate('/')}
                  class="text-sm text-gray-500 hover:text-gray-700"
                >
                  Guest? Go to app →
                </button>
              </div>
            </div>
          </Show>

          {/* Password Reset */}
          <Show when={isResetting()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button
                onClick={() => { setIsResetting(false); setResetSent(false) }}
                class="text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                ← Back to login
              </button>

              <Show when={!resetSent()}>
                <h1 class="text-xl font-bold text-gray-900 mb-2">Reset password</h1>
                <p class="text-sm text-gray-600 mb-6">
                  Enter your email and we'll send you a reset link.
                </p>

                <form onSubmit={handleResetPassword}>
                  <input
                    type="email"
                    required
                    placeholder="venue@example.com"
                    value={resetEmail()}
                    onInput={(e) => setResetEmail(e.currentTarget.value)}
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base
                           focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <button
                    type="submit"
                    class="mt-4 w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                           hover:bg-emerald-700 transition-colors"
                  >
                    Send reset link
                  </button>
                </form>
              </Show>

              <Show when={resetSent()}>
                <div class="text-center">
                  <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span class="text-3xl">📧</span>
                  </div>
                  <h1 class="text-xl font-bold text-gray-900 mb-2">Check your email</h1>
                  <p class="text-sm text-gray-600">
                    We sent a password reset link to <strong>{resetEmail()}</strong>
                  </p>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
